import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  AccountInfo,
  PersonalInfo,
  FacebookLogin,
  AppleLogin,
  GoogleButtonIcon,
  SnackBar,
  AccountInfoMenu,
  PersonalInfoMenu,
  BusinessInfoMenu,
} from '@desquared/wave-components-library';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/router';
import { OTPIDStore } from '@/store/otpid';
import CountryCodeAside from '@/components/Aside/CountryCodeAside';
import PassVerificationSideSheet from '@/components/VerificationSideSheet/PassVerificationSideSheet';
import CodeVerificationSideSheet from '@/components/VerificationSideSheet/CodeVerificationSideSheet';
import useWindowUnderThreshold from '@/hooks/useWindowUnderThreshold';
import { ResponsiveMenuStore } from '@/store/sideSheet';
import { PassVerificationSideSheetStore, OTPVerificationSideSheetStore } from '@/store/sideSheet';
import UserListsStore from '@/store/userLists';
import { PostalCode } from '@/store/isAddressRegistered';
import { LoginProviderStore } from '@/store/loginProvider';
import { CountryCodeSideSheetStore } from '@/store/sideSheet';
import { CountryCodeStore } from '@/store/countryCode';
import User from '@/store/user';
import { ModalContext } from '@/contexts/ModalProvider.jsx';
import { changePassword } from '@/api/register';
import { checkUserUpdate, updateUserV3, deleteUser } from '@/api/users';
import { seperateCountryCode } from '@/utils/countriesTransform';
import { updateUser as updateUserInContactPigeon } from '@/utils/contactPigeon';
import { logoutV3 } from '@/api/register';
import { clearCart } from '@/utils/cart';
import { createTokenCookie } from '@/utils/cookies';
import { DeliveryServicesContext } from '@/contexts/DeliveryServicesContext';

import styles from '@/styles/User.module.css';
import { Shop } from '@/store/shop';
import { shutdown } from '@intercom/messenger-js-sdk';

import { NationalityLabelStore } from '@/store/nationalityLabel';

export default function Credentials({ user, config, authorization }) {
  const t = useTranslations();
  const router = useRouter();
  const [localUser, setLocalUser] = useState(user);
  const [pass, setPass] = useState('');
  const [newPass, setNewPass] = useState('');
  const isMobile = useWindowUnderThreshold(951);
  const lengthWording = config
    ? `${config.auth.password.format.min}-${config.auth.password.format.max} ${t(
        'user_credentials.validation.characters'
      )}`
    : `8-100 ${t('user_credentials.validation.characters')}`;
  const capitalWording = t('user_credentials.validation.capitals');
  const specialCharWording = t('user_credentials.validation.specials');
  const r = useTranslations('common');

  const genders = [`${r('gender.male')}`, `${r('gender.female')}`, `${r('gender.other')}`];

  const openMenu = () => {
    ResponsiveMenuStore.open();
  };
  const [countryCode, setCountryCode] = useState(['']);
  const [nationality, setNationality] = useState(['']);

  const [showSnackBar, setShowSnackBar] = useState(false);
  const [showOtpPassVerificationSideSheet, setShowOtpVerificationSideSheet] = useState(false);
  const [showPassVerificationSideSheet, setShowPassVerificationSideSheet] = useState(false);
  const [resource, setResource] = useState(false);
  const userConfig = config?.fields?.user;
  const snackBarRef = useRef(null);

  const [openCountryCodeSideSheet, setOpenCountryCodeSideSheet] = useState(false);

  const [hasCountryCodes, setHasCountryCodes] = useState(true);

  const [snackBarText, setSnackbarText] = useState('');
  const [provider, setProvider] = useState(null);
  const [userDetails, setUserDetails] = useState({
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    mobilePhone: user.mobilePhone,
  });
  const [toChange, setToChange] = useState('');

  const verificationSubtitle =
    toChange === 'email'
      ? t('user_credentials.verification.description')
      : t('user_credentials.verification.phone.description');

  const { openActionModal, close } = useContext(ModalContext);
  const { selectedService } = useContext(DeliveryServicesContext);

  const emptySelectedShop = () => {
    if (selectedService?.deliversToAddress) {
      Shop.reset();
    }
  };

  async function onClickLogout() {
    try {
      await Promise.all([
        User.reset(),
        LoginProviderStore.clear(),
        UserListsStore.clear(),
        PostalCode.delete(),
        emptySelectedShop(),
        clearCart(),
        logoutV3(),
      ]);
    } finally {
      router.replace('/');
    }
  }
  async function onDeleteUser() {
    try {
      const response = await deleteUser(token, router.locale);
      if (response) {
        onClickLogout();
        if (config?.services?.chatbot?.enabled && config?.services?.chatbot?.type === 'INTERCOM') {
          shutdown();
        }
      }
    } catch (err) {
      router.replace('/');
    }
  }

  const showDeleteUserModal = () => {
    openActionModal({
      closeOnOuterClick: true,
      title: t('user_credentials.delete.delete_user'),
      text: t('user_credentials.delete.delete_modal_description'),
      actions: [
        {
          btnLabel: t('user_credentials.delete.back_to_main'),
          onClick: () => {
            close();
            router.replace('/');
          },
        },
        {
          btnLabel: t('user_credentials.delete.delete_user_button'),
          onClick: () => {
            onDeleteUser();
            close();
          },
          btnVariant: 'error',
        },
      ],
      hasCenteredText: true,
      img: {
        url: '/assets/wave-grocery/DeleteUser.png',
        ariaLabel: t('time_run_out_title'),
        size: 'small-medium',
      },
    });
  };

  useEffect(() => {
    const cc = localUser?.mobilePhone
      ? seperateCountryCode(localUser.mobilePhone).countryCode
      : seperateCountryCode(localUser.mobile).countryCode;
    const PassVerificationSideSheetStore$ = PassVerificationSideSheetStore.subscribe(setShowPassVerificationSideSheet);
    const OTPVerificationSideSheetStore$ = OTPVerificationSideSheetStore.subscribe(setShowOtpVerificationSideSheet);
    const user$ = User.subscribe(setLocalUser);

    const CountryCodeStore$ = CountryCodeStore.subscribe(code => setCountryCode(code ? [code] : [cc ? cc : ' ']));
    const NationalityLabelStore$ = NationalityLabelStore.subscribe(code => setNationality(code ? [code] : ['']));
    const LoginProviderStore$ = LoginProviderStore.subscribe(provider => setProvider(provider));

    return () => {
      user$.unsubscribe();
      CountryCodeStore$.unsubscribe();
      NationalityLabelStore$.unsubscribe();
      LoginProviderStore$.unsubscribe();
      PassVerificationSideSheetStore$.unsubscribe();
      OTPVerificationSideSheetStore$.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (showSnackBar && snackBarRef.current) {
      snackBarRef.current.focus();
    }
  }, [showSnackBar]);

  const onCountryCodeClick = () => {
    setHasCountryCodes(true);
    CountryCodeSideSheetStore.open();
    setOpenCountryCodeSideSheet(true);
  };

  const onNationalityClick = () => {
    setHasCountryCodes(false);
    CountryCodeSideSheetStore.open();
    setOpenCountryCodeSideSheet(true);
  };

  const { token } = user;

  async function updateUserDetails(updatedFieldsMap) {
    const updatedFields = { ...userDetails, ...updatedFieldsMap };
    setUserDetails(updatedFields);

    try {
      const response = await checkUserUpdate(token, updatedFields, router.locale);

      if (!response?.authorizationNeeded) {
        try {
          const { response: updateResponse } = await updateUserV3(token, updatedFields, router.locale);

          if (updateResponse?.data?.status === 'SUCCESS') {
            const userData = updateResponse?.data?.user || updateResponse?.user || updateResponse;

            User.update({ ...localUser, ...userData });

            if (process.env.hasContactPigeon && userData.email) {
              updateUserInContactPigeon(userData.email);
            }

            setToChange('');
            setSnackbarText(t('user_credentials.update.success.generic') || 'Changes saved!');
            setShowSnackBar(true);
          } else if (updateResponse?.data?.status === 'NEED_VERIFICATION') {
            OTPIDStore.update({
              requestId: updateResponse.data?.otpInfo.requestId,
              email: updatedFields.email,
              mobilePhone: updatedFields.mobilePhone,
            });
            OTPVerificationSideSheetStore.open();
          } else {
            const userData = updateResponse?.data?.user || updateResponse;
            User.update({ ...localUser, ...userData });
            setToChange('');
          }
        } catch (err) {
          const isPhoneError = updatedFieldsMap.hasOwnProperty('mobilePhone');
          setSnackbarText(
            isPhoneError ? t('user_credentials.personal_info.phone_error') : t('account_details.generic_error')
          );
          setShowSnackBar(true);
        }
      } else if (response?.authorizationNeeded) {
        setResource(response?.resources[0].type);
        PassVerificationSideSheetStore.open();
      }
    } catch (err) {
      console.error('Validation check failed', err);
      setSnackbarText(t('account_details.generic_error'));
      setShowSnackBar(true);
    }
  }
  // async function updateDetails(field, value) {
  //   const updatedFields = { [field]: value };
  //   const updatedUser = await updateUser(token, updatedFields);
  //   if (updatedUser?.error) {
  //     if (field === 'firstName') {
  //       setSnackbarText(t('user_credentials.update.error.firstname'));
  //       setShowSnackBar(true);
  //     } else if (field === 'lastName') {
  //       setSnackbarText(t('user_credentials.update.error.lastname'));
  //       setShowSnackBar(true);
  //     } else if (field === 'mobile.phone') {
  //       setSnackbarText(t('user_credentials.update.error.mobile'));
  //       setShowSnackBar(true);
  //     }

  //     return;
  //   }

  //   User.update(updatedUser);
  //   let fieldType;
  //   if (field === 'firstName') {
  //     fieldType = 'firstname';
  //   } else if (field === 'lastName') {
  //     fieldType = 'lastname';
  //   } else if (field === 'mobile.phone') {
  //     fieldType = 'mobile';
  //   }

  //   setSnackbarText(t('user_credentials.update.success.' + fieldType));
  //   setShowSnackBar(true);
  // }

  const onEmailSubmit = newEmail => {
    updateUserDetails({ email: newEmail });
  };
  const onPhoneSubmit = newPhone => {
    updateUserDetails({ mobilePhone: countryCode[0] + newPhone });
  };

  const onSave = (name, surname, nationality, gender, cpr, dob) => {
    const payload = {
      firstName: name,
      lastName: surname,
      ...(dob && { dateOfBirth: dob }),
      ...(nationality && { nationality: nationality }),
      ...(gender && { gender: gender }),
      ...(cpr && { idNumber: cpr }),
    };

    updateUserDetails(payload);
  };

  const onPasswordSubmit = async password => {
    const old = pass;
    const body = { password: old, newPassword: newPass };
    const res = await changePassword(body, token, router.locale);
    if (res.error) {
      if (res.error.code === 'AUTH0014') {
        setSnackbarText(t('user_credentials.auth.update_password.error.same_password'));
      } else if (res.error.code === 'AUTH0015') {
        setSnackbarText(t('user_credentials.auth.update_password.error.wrong_password'));
      } else {
        setSnackbarText(t('user_credentials.auth.update_password.success'));
      }

      setShowSnackBar(true);
      return;
    }
    createTokenCookie(res?.data?.authToken?.token);
    User.update({
      ...res?.data?.user,
      authToken: res?.data?.authToken?.token,
    });
    setToChange('');

    setSnackbarText(t('user_credentials.auth.update_password.success'));
    setShowSnackBar(true);
  };

  if (!localUser?._id) return null;
  return (
    <div className={styles.infoContainer}>
      <div className={styles.userInfoContainer}>
        {localUser?.customerType === 'b2b' && (
          <BusinessInfoMenu
            mainTitle={t('user_credentials.company_info.title')}
            credentialsWording={t('user_credentials.company_info.title')}
            businessNameWording={t('user_credentials.company_info.business_name')}
            businessAddressWording={t('user_credentials.company_info.business_address')}
            cityWording={t('common.asides.address_view.form_labels.area')}
            businessTypeWording={t('user_credentials.company_info.business_type')}
            fiscalIdWording={t('user_credentials.company_info.fiscal_id')}
            businessName={localUser?.companyName}
            businessAddress={localUser?.businessAddress}
            city={localUser?.city}
            businessType={localUser?.businessType}
            fiscalId={localUser?.fiscalId}
            isMobile={isMobile}
            onMobileBack={openMenu}
          />
        )}
        <PersonalInfoMenu
          mainTitle={t('user_credentials.personal_info.title')}
          credentialsWording={t('user_credentials.personal_info.credentials')}
          nationalityWording={t('user_credentials.personal_info.nationality')}
          genderWording={t('user_credentials.personal_info.gender')}
          cprWording={t('user_credentials.personal_info.cpr')}
          dateOfBirthWording={t('user_credentials.personal_info.dateOfBirth')}
          email={localUser.email}
          name={localUser.firstName}
          surname={localUser.lastName}
          cpr={localUser.idNumber}
          gender={localUser.gender}
          nationality={localUser.nationality}
          dob={localUser.dateOfBirth || ''}
          hasDoB={config?.auth?.profile?.registration?.userProperties[4]}
          hasGender={config?.auth?.profile?.registration?.userProperties[5]}
          hasNationality={config?.auth?.profile?.registration?.userProperties[6]}
          hasId={config?.auth?.profile?.registration?.userProperties[7]}
          buttonWording={t('user_credentials.personal_info.save')}
          isMobile={isMobile}
          onSave={onSave}
          dateFormat={process.env.dateFormat}
          nationalityArray={nationality}
          onPhoneClick={onCountryCodeClick}
          onCodeClick={onNationalityClick}
          genderOptions={genders}
          minLengthErrorText={t('common.minimun_character')}
        />
      </div>
      <AccountInfoMenu
        socialWordingStart={t('user_credentials.account_info.social_start')}
        socialWordingAnchor={t('user_credentials.account_info.social_anchor')}
        passwordConfig={config && config.auth.password.format}
        socialLoginIcon={
          provider === 'GOOGLE'
            ? GoogleButtonIcon
            : provider === 'APPLE'
            ? AppleLogin
            : provider === 'FACEBOOK'
            ? FacebookLogin
            : null
        }
        socialLink={
          provider === 'GOOGLE'
            ? 'www.google.com'
            : provider === 'APPLE'
            ? 'www.apple.com'
            : provider === 'FACEBOOK'
            ? 'www.facebook.com'
            : null
        }
        socialEmail={localUser.email}
        socialInfoWording={
          provider === 'GOOGLE'
            ? t('user_credentials.account_info.social_info', { provider: 'Google' })
            : provider === 'APPLE'
            ? t('user_credentials.account_info.social_info', { provider: 'Apple' })
            : provider === 'FACEBOOK'
            ? t('user_credentials.account_info.social_info', { provider: 'Facebook' })
            : null
        }
        accountInfoWording={t('user_credentials.account_info.title')}
        isMobile={isMobile}
        isSocialLogin={provider === 'GOOGLE' || provider === 'APPLE' || provider === 'FACEBOOK'}
        socialWordingEnd={
          provider === 'GOOGLE'
            ? t('user_credentials.account_info.social_ending', { provider: 'Google' })
            : provider === 'APPLE'
            ? t('user_credentials.account_info.social_ending', { provider: 'Apple' })
            : provider === 'FACEBOOK'
            ? t('user_credentials.account_info.social_ending', { provider: 'Facebook' })
            : null
        }
        emailWording={t('user_credentials.personal_info.email')}
        changeWording={t('user_credentials.personal_info.change')}
        passwordWording={t('user_credentials.account_info.password')}
        password={''}
        currentWording={t('user_credentials.account_info.current_password')}
        newWording={t('user_credentials.account_info.new_password')}
        charactersWording={lengthWording}
        capitalsWording={capitalWording}
        specialCharsWording={specialCharWording}
        restoreWording={t('user_credentials.account_info.restore')}
        changePasswordWording={t('user_credentials.account_info.update_password')}
        cancelWording={t('user_credentials.personal_info.cancel')}
        saveWording={t('user_credentials.personal_info.save')}
        email={localUser.email}
        onPasswordChange={password => {
          onPasswordSubmit(password);
        }}
        // onPasswordRestoreRedirect={'/reset-password'}
        onPasswordRestoreRedirect={
          router.locale === router.defaultLocale ? '/reset-password' : `/${router.locale}/reset-password`
        }
        pass={pass}
        setPass={setPass}
        newPass={newPass}
        setNewPass={setNewPass}
        phoneNumber={seperateCountryCode(localUser?.mobilePhone ? localUser.mobilePhone : localUser.mobile).phoneNumber}
        phoneWording={t('user_credentials.personal_info.phone')}
        onCodeClick={onCountryCodeClick}
        selectedCodeIndex={0}
        countryCodeLabel={t('user_credentials.country_code')}
        onOpenSideClick={onCountryCodeClick}
        countries={countryCode}
        onPhoneChange={phone => {
          onPhoneSubmit(phone);
        }}
        toChange={toChange}
        setToChange={setToChange}
        isMailEditable={!provider || provider === 'none'}
        onEmailChange={email => {
          onEmailSubmit(email);
        }}
        deleteText={t('user_credentials.delete_account')}
        onDeleteClick={showDeleteUserModal}
        showPassword={config?.auth?.verification?.registration?.items.some(el => el.type === 'EMAIL')}
        userConfig={userConfig}
        minLengthErrorText={t('common.minimun_character')}
        patternField={t('common.invalid_input')}
      />
      {showSnackBar && (
        <div
          ref={snackBarRef}
          tabIndex={-1}
          aria-live="assertive"
          aria-atomic="true"
          role="alert"
          className={styles.errorContainer}
        >
          <SnackBar
            open={showSnackBar}
            setOpen={setShowSnackBar}
            hasCloseOption={true}
            text={snackBarText}
            position={'center'}
          />
        </div>
      )}
      {openCountryCodeSideSheet && (
        <CountryCodeAside setOpenCountryCodeSideSheet={setOpenCountryCodeSideSheet} hasCountryCodes={hasCountryCodes} />
      )}
      {showPassVerificationSideSheet && (
        <PassVerificationSideSheet user={localUser} userDetails={userDetails} subTitle={verificationSubtitle} />
      )}
      {showOtpPassVerificationSideSheet && (
        <CodeVerificationSideSheet user={localUser} userDetails={userDetails} config={config} resource={resource} />
      )}
    </div>
  );
}
